import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Modern Rematch player card. Container-query sized (cqi) so it scales to its
 * width. The hero number is the stat the player actually leads (e.g. 30 GOALS)
 * — no meaningless "overall". Each category carries its own accent colour.
 */

const NUM_FONT = "'Bebas Neue', sans-serif";
const MONO_FONT = "ui-monospace, 'IBM Plex Mono', SFMono-Regular, Menlo, monospace";

const POSITION_ABBR = { forward: 'FW', striker: 'FW', midfielder: 'MF', defender: 'DF', goalkeeper: 'GK' };
const posAbbr = (p) => POSITION_ABBR[(p || '').toLowerCase()] || (p ? p.slice(0, 2).toUpperCase() : '—');
const PLATFORM_ICON = { steam: '/steam.ico', psn: '/psn.ico', xbox: '/xbox.ico' };

// Shared render placement — biased right, head sitting above the frame. The
// in-card copy is clipped by the card's overflow; the "breakout" copy is a
// sibling clipped to just the head so it pops above the top edge.
const RENDER_POS = { width: '110%', height: 'auto', right: '-17%', top: '-12%' };
const HEAD_CLIP = 'inset(0% 10% 74% 33%)';
const RESERVE = '15%'; // wrapper top margin so the head has room to break out
const STRIPE = '/player-card/rematch-banner.webp'; // scrolling REMATCH banner

// hex -> rgba
const rgba = (hex, a) => {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

// Mouse-follow 3D tilt (sets --parallax vars read by .card-tilt).
const Tilt = ({ children }) => {
  const ref = useRef(null);
  const reduce = useRef(false);
  useEffect(() => {
    reduce.current = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }, []);
  const set = (x, y) => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty('--parallax-x', x.toFixed(3));
    el.style.setProperty('--parallax-y', y.toFixed(3));
  };
  const onMove = (e) => {
    if (reduce.current) return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    set(((e.clientX - r.left) / r.width) * 2 - 1, ((e.clientY - r.top) / r.height) * 2 - 1);
  };
  return (
    <div ref={ref} className="card-tilt" onMouseMove={onMove} onMouseLeave={() => set(0, 0)}>
      {children}
    </div>
  );
};

const PlayerCard = ({ player, badge, accent = '#31C590', highlight, width = 190, className = '', imageUrl }) => {
  const [imgError, setImgError] = useState(false);
  if (!player) return null;

  const name = (player.display_name || player.username || 'Player').toUpperCase();
  const image = imgError ? null : (imageUrl || player.image_url || null);
  const platformIcon = PLATFORM_ICON[(player.platform || '').toLowerCase()];
  const winRate = Math.round(player.win_rate ?? (player.total_matches ? ((player.total_wins ?? 0) / player.total_matches) * 100 : 0));

  const hero = highlight || { value: player.total_goals ?? 0, label: 'GOALS', key: 'total_goals' };

  const stats = [
    { key: 'total_goals', v: player.total_goals ?? 0, l: 'GOALS' },
    { key: 'total_assists', v: player.total_assists ?? 0, l: 'ASSISTS' },
    { key: 'total_passes', v: player.total_passes ?? 0, l: 'PASSES' },
    { key: 'total_interceptions', v: player.total_interceptions ?? 0, l: 'INTERC.' },
    { key: 'total_saves', v: player.total_saves ?? 0, l: 'SAVES' },
    { key: 'mvp_count', v: player.mvp_count ?? 0, l: 'MVPS' }
  ];

  const card = (
    <div style={{ containerType: 'inline-size', width, position: 'relative', marginTop: RESERVE }}>
      <div
        className="relative overflow-hidden select-none"
        style={{
          aspectRatio: '5 / 7',
          borderRadius: '6cqi',
          background: 'linear-gradient(180deg, #141924 0%, #0a0d14 62%)',
          boxShadow: `inset 0 0 0 0.5cqi ${rgba(accent, 0.35)}, 0 3cqi 10cqi rgba(0,0,0,0.35)`
        }}
      >
        {/* accent glow top */}
        <div aria-hidden className="absolute inset-x-0 top-0 pointer-events-none" style={{ height: '55%', zIndex: 1, background: `radial-gradient(120% 90% at 50% 0%, ${rgba(accent, 0.5)} 0%, ${rgba(accent, 0)} 65%)` }} />

        {/* base fade for legibility (SITS BELOW the banner so it never masks it) */}
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(10,13,20,0) 34%, rgba(10,13,20,0.72) 52%, #0a0d14 68%)', zIndex: 2 }} />

        {/* scrolling REMATCH banner on the right edge — above the card gradients
            so nothing masks it, behind the player (two stacked copies loop).
            Faded at the bottom to match where the player render fades out. */}
        <div
          aria-hidden
          className="absolute pointer-events-none overflow-hidden"
          style={{
            top: '-2cqi', right: '-3cqi', bottom: '-2cqi', width: '35cqi', zIndex: 3,
            maskImage: 'linear-gradient(to bottom, #000 47%, transparent 70%)',
            WebkitMaskImage: 'linear-gradient(to bottom, #000 47%, transparent 70%)'
          }}
        >
          <div style={{ animation: 'card-banner-scroll 20s linear infinite', willChange: 'transform' }}>
            <img src={STRIPE} alt="" style={{ display: 'block', width: '100%' }} />
            <img src={STRIPE} alt="" style={{ display: 'block', width: '100%' }} />
          </div>
        </div>

        {/* category label (behind the player so the character overlaps it) */}
        {badge && (
          <div
            className="absolute inline-flex items-center font-bold uppercase"
            style={{
              top: '4cqi', left: '4cqi', padding: '1.4cqi 3cqi', borderRadius: '2.4cqi', zIndex: 4,
              fontSize: '6.6cqi', letterSpacing: '0.06em', color: accent,
              backgroundColor: rgba(accent, 0.14), boxShadow: `inset 0 0 0 0.4cqi ${rgba(accent, 0.4)}`,
              backdropFilter: 'blur(4px)'
            }}
          >
            {badge}
          </div>
        )}

        {/* player render — above the banner + badge so the character sits in front */}
        {image && (
          <img
            src={image}
            alt=""
            onError={() => setImgError(true)}
            className="absolute pointer-events-none"
            style={{
              ...RENDER_POS, zIndex: 5,
              maskImage: 'linear-gradient(to bottom, #000 50%, transparent 72%)',
              WebkitMaskImage: 'linear-gradient(to bottom, #000 50%, transparent 72%)'
            }}
          />
        )}

        {/* content */}
        <div className="absolute inset-x-0 bottom-0 flex flex-col" style={{ padding: '5cqi', gap: '2.4cqi', zIndex: 6 }}>
          {/* hero stat */}
          <div className="flex items-end" style={{ gap: '2.4cqi', lineHeight: 0.8 }}>
            <span style={{ fontFamily: NUM_FONT, fontSize: '34cqi', color: accent, lineHeight: 0.8 }}>{hero.value}</span>
            <span style={{ fontFamily: MONO_FONT, fontSize: '7cqi', fontWeight: 600, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', marginBottom: '2cqi' }}>{hero.label}</span>
          </div>

          {/* name + flag + platform */}
          <div className="flex items-center" style={{ gap: '2.6cqi' }}>
            {player.country_code && (
              <img
                src={`https://flagcdn.com/24x18/${(player.country_code || '').toLowerCase()}.png`}
                alt=""
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                style={{ width: '13cqi', height: 'auto', borderRadius: '1cqi' }}
              />
            )}
            {platformIcon && <img src={platformIcon} alt="" style={{ width: '9cqi', height: '9cqi', opacity: 0.85 }} />}
            <span className="truncate" style={{ fontFamily: NUM_FONT, fontSize: '16cqi', color: 'white', letterSpacing: '0.02em', lineHeight: 1 }}>{name}</span>
          </div>

          {/* meta chips */}
          <div className="flex items-center" style={{ gap: '2cqi', fontFamily: MONO_FONT, fontSize: '6.4cqi', fontWeight: 600 }}>
            <span style={{ padding: '1cqi 2.4cqi', borderRadius: '1.6cqi', backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.8)' }}>{posAbbr(player.position)}</span>
            <span style={{ padding: '1cqi 2.4cqi', borderRadius: '1.6cqi', backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.8)' }}>{winRate}% WIN</span>
            <span style={{ padding: '1cqi 2.4cqi', borderRadius: '1.6cqi', backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.8)' }}>{player.tournaments_won ?? 0} TITLES</span>
          </div>

          {/* divider */}
          <div style={{ height: '0.4cqi', background: 'rgba(255,255,255,0.1)', margin: '1cqi 0' }} />

          {/* stat grid — 3×2, vertical cells (never wraps) */}
          <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', rowGap: '2.4cqi', columnGap: '1cqi' }}>
            {stats.map((s) => {
              const lead = s.key === hero.key;
              return (
                <div key={s.key} className="flex flex-col items-center text-center">
                  <span style={{ fontFamily: NUM_FONT, fontSize: '14cqi', lineHeight: 0.9, color: lead ? accent : 'white' }}>{s.v}</span>
                  <span style={{ fontFamily: MONO_FONT, fontSize: '5.6cqi', fontWeight: 500, letterSpacing: '0.04em', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', marginTop: '0.8cqi' }}>{s.l}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Head "breakout" — a copy of the render clipped to the head, popping
          above the card's top edge (not clipped by the card's overflow). */}
      {image && (
        <img
          src={image}
          alt=""
          aria-hidden
          onError={() => setImgError(true)}
          className="absolute pointer-events-none"
          style={{ ...RENDER_POS, clipPath: HEAD_CLIP, WebkitClipPath: HEAD_CLIP, zIndex: 6 }}
        />
      )}
    </div>
  );

  const pid = player.user_id || player.player_id;
  const to = pid ? `/profile/${pid}` : '/leaderboards';
  return (
    <Link to={to} className={`block group ${className}`} aria-label={name} style={{ width }}>
      <Tilt>{card}</Tilt>
    </Link>
  );
};

export default PlayerCard;
